import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ImpalaCopySource } from 'app/model/pipeline/copysource/impala.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { GlobalVariables } from 'app/services/globals';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ImpalaCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        batchSize: GlobalVariables.enableImpalaV2BatchSize ? '*' : undefined,
        queryTimeout: GlobalVariables.enableImpalaV2QueryTimeout ? '*' : undefined
    }
})
export class ImpalaCopySourceAdapter extends CopySourceAdapter<ImpalaCopySource, Model.ImpalaSource> {

    protected override async _toResource(copySource: ImpalaCopySource): Promise<Model.ImpalaSource> {
        const result = await this._toTabularResource(copySource, Metamodel.modelFor(ImpalaCopySource).name);
        if (GlobalVariables.enableImpalaV2BatchSize) {
            result.batchSize = ValueAdapter.toResource(copySource, ImpalaCopySource.BATCH_SIZE);
        }
        if (GlobalVariables.enableImpalaV2QueryTimeout) {
            try {
                result.queryTimeout =
                    typeof copySource.queryTimeout === 'number'
                        ? moment.duration(copySource.queryTimeout, 'seconds').format('hh:mm:ss', { trim: false })
                        : ValueAdapter.toResource(copySource, ImpalaCopySource.QUERY_TIMEOUT);
            } catch {
                this._logError('Failed to convert queryTimeout into TimeSpan');
            }
        }
        return result;
    }

    protected override async _toModel(resource: Model.ImpalaSource, copyActivity: CopyActivity): Promise<ImpalaCopySource> {
        const result = (await this._toTabularModel(resource, copyActivity, ImpalaCopySource.MODEL)) as ImpalaCopySource;
        if (GlobalVariables.enableImpalaV2BatchSize) {
            ValueAdapter.toModel(result, ImpalaCopySource.BATCH_SIZE, resource.batchSize);
        }
        if (GlobalVariables.enableImpalaV2QueryTimeout) {
            try {
                if (resource.queryTimeout == null) {
                    result.queryTimeout = undefined;
                } else if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asSeconds();
                } else {
                    ValueAdapter.toModel(result, ImpalaCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            } catch {
                this._logError('Failed to convert queryTimeout into number');
            }
        }
        return result;
    }
}
